import React, { useId, useState } from "react";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { ALL_COLUMNS, ColumnDef, SECTOR_LABEL, SectorId } from "./columns";
import { ColumnTooltip, getColumnMeta, SourceBadge } from "./columnMeta";

interface ColumnVisibilityMenuProps {
  visibleIds: string[];
  onChange: (ids: string[]) => void;
  /** Columns offered (default: the whole registry). */
  columns?: ColumnDef[];
}

const SECTORS_ORDER: SectorId[] = [
  "identification",
  "brief",
  "kpis",
  "alert",
  "pacing",
  "others",
];

/** Groups columns by sector maintaining declared order */
function groupBySector(cols: ColumnDef[]): Map<SectorId, ColumnDef[]> {
  const map = new Map<SectorId, ColumnDef[]>();
  SECTORS_ORDER.forEach((s) => map.set(s, []));
  cols.forEach((c) => {
    map.get(c.sector)?.push(c);
  });
  return map;
}

const ColumnVisibilityMenu: React.FC<ColumnVisibilityMenuProps> = ({
  visibleIds,
  onChange,
  columns = ALL_COLUMNS,
}) => {
  const buttonId = useId();
  const popoverId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const grouped = groupBySector(columns);

  const toggle = (id: string) => {
    const next = visibleIds.includes(id)
      ? visibleIds.filter((v) => v !== id)
      : [...visibleIds, id];
    onChange(next);
  };

  const toggleSector = (sector: SectorId) => {
    const sectorCols = grouped.get(sector) ?? [];
    const sectorIds = sectorCols.map((c) => c.id);
    const allVisible = sectorIds.every((id) => visibleIds.includes(id));

    if (allVisible) {
      // hide all in this sector
      onChange(visibleIds.filter((id) => !sectorIds.includes(id)));
    } else {
      // show all in this sector
      const next = [...new Set([...visibleIds, ...sectorIds])];
      onChange(next);
    }
  };

  const getSectorState = (sector: SectorId): "all" | "some" | "none" => {
    const sectorCols = grouped.get(sector) ?? [];
    if (sectorCols.length === 0) return "none";
    const visCount = sectorCols.filter((c) => visibleIds.includes(c.id)).length;
    if (visCount === 0) return "none";
    if (visCount === sectorCols.length) return "all";
    return "some";
  };

  return (
    <>
      <IconButton
        id={buttonId}
        aria-controls={open ? popoverId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        size="small"
        title="Configurar columnas"
      >
        <SettingsIcon fontSize="small" />
      </IconButton>

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              minWidth: 280,
              maxHeight: 520,
              overflowY: "auto",
            },
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Columnas visibles
        </Typography>

        {SECTORS_ORDER.map((sector, idx) => {
          const cols = grouped.get(sector) ?? [];
          if (cols.length === 0) return null;
          const sectorState = getSectorState(sector);

          return (
            <Box key={sector}>
              {idx > 0 && <Divider sx={{ my: 1 }} />}

              {/* Sector header with tri-state checkbox */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sectorState === "all"}
                    indeterminate={sectorState === "some"}
                    onChange={() => toggleSector(sector)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {SECTOR_LABEL[sector]}
                  </Typography>
                }
                sx={{ display: "flex", ml: 0 }}
              />

              {/* Individual column checkboxes */}
              <Box sx={{ ml: 2 }}>
                {cols.map((col) => {
                  const meta = getColumnMeta(col.id);
                  return (
                    <FormControlLabel
                      key={col.id}
                      control={
                        <Checkbox
                          checked={visibleIds.includes(col.id)}
                          onChange={() => toggle(col.id)}
                          size="small"
                        />
                      }
                      label={
                        <Tooltip
                          title={
                            <ColumnTooltip
                              label={col.label}
                              source={meta.source}
                              description={meta.description}
                              inputs={meta.inputs}
                            />
                          }
                          placement="left"
                          enterDelay={350}
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <SourceBadge source={meta.source} />
                            <Typography variant="body2">{col.label}</Typography>
                          </Box>
                        </Tooltip>
                      }
                      sx={{ display: "flex", ml: 0 }}
                    />
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Popover>
    </>
  );
};

export default ColumnVisibilityMenu;
